/**
 * Theme control — DESIGN.md: "The theme control lives in Settings → Account →
 * Appearance (Auto / Light / Dark) and writes data-theme on <html>."
 *
 * The app DEFAULTS TO LIGHT: with nothing stored, `readTheme()` returns
 * 'light', so a viewer whose OS prefers dark still sees the light design on
 * first visit. Auto stays a choice in Settings; choosing it writes no
 * attribute, so tokens.css falls through to the OS preference block.
 *
 * Keep this in step with the pre-paint script in index.html, which applies the
 * same default before React loads so there is no flash of the wrong theme.
 *
 * Only background-color and color transition on flip.
 */
export type Theme = 'auto' | 'light' | 'dark'

const STORAGE_KEY = 'theme'

/** The theme a viewer sees before they have chosen one. */
export const DEFAULT_THEME: Theme = 'light'

export function readTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'auto') return stored
  } catch {
    // Private mode or blocked storage — fall through to the default.
  }
  return DEFAULT_THEME
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  if (theme === 'auto') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', theme)

  try {
    // 'auto' is a deliberate choice, so it is stored like any other — leaving
    // it unstored would silently re-apply the light default on the next load.
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // Nothing to do; the attribute is already applied for this session.
  }
}

/** True when the rendered theme is dark, whatever the setting is. */
export function isDark(): boolean {
  const attr = document.documentElement.getAttribute('data-theme')
  if (attr === 'dark') return true
  if (attr === 'light') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}
